import { useState, useEffect, useRef } from 'react';
import { withFileAuth } from '@/utils/api';
import { idem } from '@/utils/api';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { useWebSocket } from '@/contexts/WebSocketContext';
import { useNotificationSound } from '@/hooks/useNotificationSound';
import { TableMap } from '@/components/tables/TableMap';
import { TableActionsDialog } from '@/components/tables/TableActionsDialog';
import { useTableStates } from '@/components/tables/useTableStates';
import ModernPOS from '@/modern/pos/ModernPOS';
import { useIsModern } from '@/modern/shared';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Switch } from '@/components/ui/switch';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  ArrowLeft, 
  Clock,
  DollarSign,
  CheckCircle,
  XCircle,
  Send,
  MessageSquare,
  CreditCard,
  Printer,
  RefreshCw,
  Bell,
  Users,
  ShoppingCart,
  ChefHat,
  Volume2,
  VolumeX,
  Shield,
  Plus,
  Filter,
  LayoutGrid,
  Map,
  Maximize2,
  Eye,
  LogIn,
  LogOut as LogOutIcon,
  Loader2
} from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';
import { inkOn } from '@/theme/statusColors';

// Use relative path for API calls (works on any domain)
const API = typeof window !== 'undefined' && window.location.hostname !== 'localhost' 
  ? '/api' 
  : `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function POSPanel() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { connected, lastMessage, subscribe } = useWebSocket();
  const { playSound, toggleSounds } = useNotificationSound();
  
  const [spaces, setSpaces] = useState([]);
  const [selectedSpace, setSelectedSpace] = useState(null);
  const [units, setUnits] = useState([]);
  const [activeOrders, setActiveOrders] = useState([]);
  const [statuses, setStatuses] = useState([]);
  const [activeSessions, setActiveSessions] = useState([]);
  const [selectedUnit, setSelectedUnit] = useState(null);
  const [unitDetails, setUnitDetails] = useState(null);
  const [loading, setLoading] = useState(true);
  const [soundEnabled, setSoundEnabled] = useState(() => {
    return localStorage.getItem('soundsEnabled') !== 'false';
  });
  const [newOrderIds, setNewOrderIds] = useState(new Set());
  const previousOrdersRef = useRef([]);
  
  // Dialog states
  const [showCheckoutDialog, setShowCheckoutDialog] = useState(false);
  const [showWhatsAppDialog, setShowWhatsAppDialog] = useState(false);
  const [showAddItemDialog, setShowAddItemDialog] = useState(false);
  const [checkoutTip, setCheckoutTip] = useState(0);
  const [paymentMethod, setPaymentMethod] = useState('cash');
  const [whatsAppPhone, setWhatsAppPhone] = useState('');
  const [allUnits, setAllUnits] = useState([]);
  const [menu, setMenu] = useState({ categories: [], products: [] });
  const [selectedProducts, setSelectedProducts] = useState([]);
  const [addingItems, setAddingItems] = useState(false);
  const [viewMode, setViewMode] = useState('grid'); // 'grid' or 'map'
  const [mapImages, setMapImages] = useState({}); // space_id -> map_image_url
  const [printingTicket, setPrintingTicket] = useState(false);
  const [showTicketPreview, setShowTicketPreview] = useState(false);
  const [ticketHtml, setTicketHtml] = useState('');
  const [showMapFullscreen, setShowMapFullscreen] = useState(false);
  const [creatingSession, setCreatingSession] = useState(null);
  const [closingSession, setClosingSession] = useState(null);
  const [showUnitActions, setShowUnitActions] = useState(null);
  const [actionUnit, setActionUnit] = useState(null);
  const { units: tableUnits, decor: tableDecor, loaded: tablesLoaded, refresh: refreshTables, now: tablesNow } = useTableStates();
  const isModern = useIsModern();
  const canSeeAmounts = ['business_admin', 'cashier', 'super_admin'].includes(user?.role);
  const toLegacyUnit = (u) => ({ ...u, unit_id: u.group_primary_unit_id || u.unit_id, current_session_id: u.session_id, status: u.session_id ? 'occupied' : 'available' });
  const [params] = useSearchParams();
  const linkedUnit = params.get('unit');
  const [posTab, setPosTab] = useState('orders');
  useEffect(() => {
    if (isModern || !tablesLoaded || !linkedUnit) return;
    const u = tableUnits.find(x => x.unit_id === linkedUnit);
    if (u) handleUnitClick(toLegacyUnit(u));
  }, [isModern, tablesLoaded, linkedUnit]); // eslint-disable-line react-hooks/exhaustive-deps
  
  useEffect(() => {
    fetchDashboard();
  }, []);

  useEffect(() => {
    // Subscribe to real-time updates
    const unsubscribe = subscribe('*', (message) => {
      if (message.type === 'new_order') {
        if (soundEnabled) {
          playSound('newOrder');
        }
        // Add to new order IDs for animation
        if (message.order?.order_id) {
          setNewOrderIds(prev => new Set([...prev, message.order.order_id]));
          setTimeout(() => {
            setNewOrderIds(prev => {
              const newSet = new Set(prev);
              newSet.delete(message.order.order_id);
              return newSet;
            });
          }, 3000);
        }
        toast.info('¡Nuevo pedido!', {
          description: `${message.order.items?.length || 0} items`
        });
        fetchDashboard();
      } else if (message.type === 'order_status_update') {
        if (message.new_status === 'Listo' && soundEnabled) {
          playSound('orderReady');
        }
        setActiveOrders(prev => prev.map(o => 
          o.order_id === message.order_id 
            ? { ...o, status: message.new_status }
            : o
        ));
      } else if (message.type === 'session_closed') {
        fetchDashboard();
      }
    });

    return () => unsubscribe();
  }, [subscribe, soundEnabled, playSound]);

  const handleToggleSound = (enabled) => {
    setSoundEnabled(enabled);
    toggleSounds(enabled);
  };

  const fetchDashboard = async () => {
    try {
      const response = await axios.get(`${API}/pos/dashboard`, { withCredentials: true });
      setSpaces(response.data.spaces || []);
      
      const newOrders = response.data.active_orders || [];
      
      // Check for new orders (comparing with previous)
      if (previousOrdersRef.current.length > 0) {
        const previousIds = new Set(previousOrdersRef.current.map(o => o.order_id));
        const brandNewOrders = newOrders.filter(o => !previousIds.has(o.order_id));
        
        if (brandNewOrders.length > 0 && soundEnabled) {
          playSound('newOrder');
          setNewOrderIds(new Set(brandNewOrders.map(o => o.order_id)));
          setTimeout(() => setNewOrderIds(new Set()), 3000);
        }
      }
      previousOrdersRef.current = newOrders;
      
      setActiveOrders(newOrders);
      setStatuses(response.data.statuses || []);
      setActiveSessions(response.data.active_sessions || []);
      
      // Collect all units from all spaces with positions
      const all = response.data.spaces?.flatMap(space => 
        space.units.map(u => ({ 
          ...u, 
          space_name: space.name,
          space_id: space.space_id
        }))
      ) || [];
      setAllUnits(all);
      
      // Collect map images from spaces
      const maps = {};
      response.data.spaces?.forEach(space => {
        if (space.map_image_url) {
          maps[space.space_id] = space.map_image_url;
        }
      });
      setMapImages(maps);
      
      if (response.data.spaces?.length > 0 && !selectedSpace) {
        setSelectedSpace('all'); // Default to show all
        setUnits(all);
      }
    } catch (error) {
      console.error('Error fetching dashboard:', error);
      toast.error('Error al cargar datos');
    } finally {
      setLoading(false);
    }
  };

  const handleSpaceChange = (spaceId) => {
    setSelectedSpace(spaceId);
    if (spaceId === 'all') {
      setUnits(allUnits);
    } else {
      const space = spaces.find(s => s.space_id === spaceId);
      setUnits(space?.units || []);
    }
    setSelectedUnit(null);
    setUnitDetails(null);
  };

  const handleUnitClick = async (unit) => {
    setSelectedUnit(unit);
    
    if (unit.current_session_id) {
      try {
        const response = await axios.get(
          `${API}/sessions/${unit.current_session_id}`,
          { withCredentials: true }
        );
        setUnitDetails(response.data);
        setPosTab('details');
      } catch (error) {
        console.error('Error fetching session:', error);
      }
    } else {
      setUnitDetails(null);
    }
  };

  const handleStatusChange = async (orderId, newStatus) => {
    try {
      await axios.put(
        `${API}/orders/${orderId}/status`,
        { status: newStatus },
        { withCredentials: true }
      );
      
      setActiveOrders(prev => prev.map(o => 
        o.order_id === orderId ? { ...o, status: newStatus } : o
      ));
      
      // Update unit details if showing
      if (unitDetails?.orders) {
        setUnitDetails({
          ...unitDetails,
          orders: unitDetails.orders.map(o => 
            o.order_id === orderId ? { ...o, status: newStatus } : o
          )
        });
      }
      
      toast.success('Estado actualizado');
    } catch (error) {
      toast.error('Error al actualizar estado');
    }
  };

  const handleCancelOrder = async (orderId) => {
    try {
      await axios.post(
        `${API}/orders/${orderId}/cancel`,
        {},
        { withCredentials: true }
      );
      toast.success('Pedido cancelado');
      fetchDashboard();
    } catch (error) {
      toast.error('Error al cancelar pedido');
    }
  };

  const handleCheckout = async () => {
    if (!unitDetails?.session_id) return;
    
    try {
      await axios.post(
        `${API}/sessions/${unitDetails.session_id}/close?tip=${checkoutTip}&payment_method=${paymentMethod}`,
        {},
        { withCredentials: true, ...idem() }
      );
      
      toast.success('Sesión cerrada exitosamente');
      setShowCheckoutDialog(false);
      setSelectedUnit(null);
      setUnitDetails(null);
      fetchDashboard();
    } catch (error) {
      toast.error('Error al cerrar sesión');
    }
  };

  // Print ticket function - shows preview first based on settings
  const handlePrintTicket = async () => {
    if (!unitDetails?.session_id) return;
    
    setPrintingTicket(true);
    try {
      // Get settings to check if preview is enabled
      let showPreview = true;
      try {
        const settingsRes = await axios.get(`${API}/business/settings`);
        showPreview = settingsRes.data?.show_print_preview !== false;
      } catch (e) {}
      
      const response = await axios.post(
        `${API}/print/ticket/${unitDetails.session_id}`,
        { include_tips_suggestion: true },
        { withCredentials: true }
      );
      
      const { html } = response.data;
      
      if (html) {
        if (showPreview) {
          setTicketHtml(html);
          setShowTicketPreview(true);
        } else {
          const printWindow = window.open('', '_blank', 'width=400,height=600');
          printWindow.document.write(html);
          printWindow.document.close();
        }
      } else {
        toast.info('No se pudo generar el ticket');
      }
    } catch (error) {
      console.error('Print error:', error);
      toast.error('Error al generar ticket');
    } finally {
      setPrintingTicket(false);
    }
  };

  // Actually print after preview
  const handleConfirmPrint = () => {
    if (!ticketHtml) return;
    
    const printWindow = window.open('', '_blank', 'width=400,height=600');
    printWindow.document.write(ticketHtml);
    printWindow.document.close();
    setShowTicketPreview(false);
    setTicketHtml('');
  };

  // Create session (occupy table)
  const handleCreateSession = async (unit) => {
    setCreatingSession(unit.unit_id);
    try {
      await axios.post(
        `${API}/sessions`,
        { unit_id: unit.unit_id },
        { withCredentials: true }
      );
      toast.success(`${unit.name} ocupada`);
      fetchDashboard();
    } catch (error) {
      toast.error('Error al ocupar mesa');
    } finally {
      setCreatingSession(null);
      setShowUnitActions(null);
    }
  };

  // Quick release session (free table without payment - for mistakes)
  const handleQuickRelease = async (unit) => {
    if (!window.confirm(`¿Liberar ${unit.name}? Esta acción no registra un pago.`)) return;
    
    setClosingSession(unit.unit_id);
    try {
      // Find the session for this unit
      const session = activeSessions.find(s => s.unit_id === unit.unit_id);
      if (session) {
        await axios.post(
          `${API}/sessions/${session.session_id}/close?tip=0&payment_method=none`,
          {},
          { withCredentials: true, ...idem() }
        );
      }
      toast.success(`${unit.name} liberada`);
      fetchDashboard();
    } catch (error) {
      toast.error('Error al liberar mesa');
    } finally {
      setClosingSession(null);
      setShowUnitActions(null);
    }
  };

  // Double click handler for quick toggle
  const handleUnitDoubleClick = async (unit, e) => {
    e.preventDefault();
    e.stopPropagation();
    
    if (unit.status === 'available' || !unit.current_session_id) {
      // Occupy
      await handleCreateSession(unit);
    } else {
      // Show details
      handleUnitClick(unit);
    }
  };

  const handleSendWhatsApp = async () => {
    if (!unitDetails?.session_id || !whatsAppPhone) return;
    
    try {
      const response = await axios.post(
        `${API}/sessions/${unitDetails.session_id}/send-bill-whatsapp`,
        { 
          phone_number: whatsAppPhone,
          session_id: unitDetails.session_id 
        },
        { withCredentials: true }
      );
      
      if (response.data.wa_link) {
        window.open(response.data.wa_link, '_blank');
      }
      
      toast.success('Link de WhatsApp generado');
      setShowWhatsAppDialog(false);
    } catch (error) {
      toast.error('Error al enviar WhatsApp');
    }
  };

  const fetchMenu = async () => {
    try {
      const response = await axios.get(`${API}/menu`, { withCredentials: true });
      setMenu({
        categories: response.data.categories || [],
        products: response.data.products || []
      });
    } catch (error) {
      console.error('Error fetching menu:', error);
    }
  };

  const openAddItemDialog = async () => {
    if (menu.products.length === 0) {
      await fetchMenu();
    }
    setSelectedProducts([]);
    setShowAddItemDialog(true);
  };

  const toggleProductSelection = (product) => {
    setSelectedProducts(prev => {
      const existing = prev.find(p => p.product_id === product.product_id);
      if (existing) {
        if (existing.quantity > 1) {
          return prev.map(p => p.product_id === product.product_id 
            ? { ...p, quantity: p.quantity - 1 } 
            : p
          );
        }
        return prev.filter(p => p.product_id !== product.product_id);
      }
      return [...prev, { ...product, quantity: 1 }];
    });
  };

  const incrementProduct = (productId) => {
    setSelectedProducts(prev => prev.map(p => 
      p.product_id === productId ? { ...p, quantity: p.quantity + 1 } : p
    ));
  };

  const handleAddItemsToSession = async () => {
    if (!unitDetails?.session_id || selectedProducts.length === 0) return;
    
    setAddingItems(true);
    try {
      const items = selectedProducts.map(p => ({
        product_id: p.product_id,
        quantity: p.quantity,
        variant: null,
        extras: [],
        notes: ''
      }));

      await axios.post(
        `${API}/sessions/${unitDetails.session_id}/orders`,
        { items },
        { withCredentials: true, ...idem() }
      );

      toast.success('Ítems agregados correctamente');
      setShowAddItemDialog(false);
      setSelectedProducts([]);
      
      // Refresh the session details
      const response = await axios.get(
        `${API}/sessions/${unitDetails.session_id}`,
        { withCredentials: true }
      );
      setUnitDetails(response.data);
      fetchDashboard();
    } catch (error) {
      toast.error('Error al agregar ítems');
    } finally {
      setAddingItems(false);
    }
  };

  const getStatusColor = (status) => {
    const statusConfig = statuses.find(s => s.name === status);
    return statusConfig?.color || '#6B7280';
  };

  const getUnitStatusStyle = (status) => {
    switch (status) {
      case 'available': return 'bg-green-100 border-green-500 text-green-800';
      case 'occupied': return 'bg-yellow-100 border-yellow-500 text-yellow-800';
      case 'consuming': return 'bg-blue-100 border-blue-500 text-blue-800';
      case 'pending_payment': return 'bg-red-100 border-red-500 text-red-800';
      default: return 'bg-gray-100 border-gray-400 text-gray-800';
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="w-12 h-12 border-4 border-primary border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      {isModern ? (
        <ModernPOS spaces={spaces} units={tableUnits} decor={tableDecor} now={tablesNow} user={user} canSeeAmounts={canSeeAmounts} initialUnitId={linkedUnit}
          details={unitDetails} opening={creatingSession}
          loadDetails={async (u) => {
            setSelectedUnit(toLegacyUnit(u));
            if (!u.session_id) return setUnitDetails(null);
            try { setUnitDetails((await axios.get(`${API}/sessions/${u.session_id}`)).data); } catch { setUnitDetails(null); }
          }}
          onOpenUnit={async (u) => { await handleCreateSession({ unit_id: u.unit_id, name: u.name }); refreshTables(); }}
          onAddItem={openAddItemDialog} onStatusChange={handleStatusChange} onPrint={handlePrintTicket}
          checkout={{ open: showCheckoutDialog, setOpen: setShowCheckoutDialog, tip: checkoutTip, setTip: setCheckoutTip,
            method: paymentMethod, setMethod: setPaymentMethod, confirm: async () => { await handleCheckout(); refreshTables(); } }} />
      ) : (<>
      {/* Header */}
      <header className="sticky top-0 z-50 bg-card border-b border-border px-4 md:px-6 h-16 flex items-center justify-between">
        <div className="flex items-center gap-4">
          {/* Back to Super Admin */}
          {user?.role === 'super_admin' && (
            <Button 
              variant="ghost" 
              size="icon"
              onClick={() => navigate('/super-admin')}
              title="Volver a Super Admin"
            >
              <Shield className="w-5 h-5" />
            </Button>
          )}
          <Button variant="ghost" size="icon" onClick={() => navigate('/dashboard')}>
            <ArrowLeft className="w-5 h-5" />
          </Button>
          <div>
            <h1 className="font-heading text-lg font-semibold">Panel POS</h1>
            <p className="text-sm text-muted-foreground">
              Gestión de pedidos en tiempo real
            </p>
          </div>
        </div>

        <div className="flex items-center gap-3">
          <div className={`flex items-center gap-2 px-3 py-1.5 rounded-full text-sm ${
            connected ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'
          }`}>
            <div className={`w-2 h-2 rounded-full ${connected ? 'bg-green-500 animate-pulse' : 'bg-red-500'}`} />
            {connected ? 'En vivo' : 'Desconectado'}
          </div>
          
          {/* Sound Toggle */}
          <Button
            variant="outline"
            size="icon"
            onClick={() => handleToggleSound(!soundEnabled)}
            title={soundEnabled ? 'Desactivar sonido' : 'Activar sonido'}
          >
            {soundEnabled ? (
              <Volume2 className="w-4 h-4 text-green-600" />
            ) : (
              <VolumeX className="w-4 h-4 text-muted-foreground" />
            )}
          </Button>
          
          <Button variant="outline" size="icon" onClick={fetchDashboard}>
            <RefreshCw className="w-4 h-4" />
          </Button>
        </div>
      </header>

      <div className="flex h-[calc(100vh-64px)]">
        {/* Left Panel - Layout View */}
        <div className="flex-1 p-4 md:p-6 overflow-auto">
          {/* Space Selector */}
          <div className="flex items-center gap-4 mb-6 flex-wrap">
            <Select value={selectedSpace || 'all'} onValueChange={handleSpaceChange}>
              <SelectTrigger className="w-64" data-testid="space-selector">
                <Filter className="w-4 h-4 mr-2" />
                <SelectValue placeholder="Seleccionar espacio" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">
                  Todos los espacios ({allUnits.length})
                </SelectItem>
                {spaces.map((space) => (
                  <SelectItem key={space.space_id} value={space.space_id}>
                    {space.name} ({space.units?.length || 0})
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>

            <div className="w-full">
              {tablesLoaded && (
                <TableMap
                  key={selectedSpace}
                  units={selectedSpace === 'all' ? tableUnits : tableUnits.filter(u => u.space_id === selectedSpace)}
                  mapImage={selectedSpace !== 'all' ? mapImages[selectedSpace] : null}
                  showAmount={canSeeAmounts}
                  now={tablesNow}
                  selectedId={actionUnit?.unit_id}
                  onSelect={(u) => setActionUnit(u)}
                  onDoubleClick={(u) => handleUnitDoubleClick(toLegacyUnit(u), { stopPropagation: () => {} })}
                  onClean={async (u) => { try { await axios.post(`${API}/tables/${u.unit_id}/clean`); toast.success('Mesa lista'); refreshTables(); } catch { toast.error('No se pudo marcar'); } }}
                />
              )}
            </div>
          </div>
        </div>

        {/* Right Panel - Orders & Details */}
        <div className="w-96 border-l border-border bg-card overflow-hidden flex flex-col h-full">
          <Tabs value={posTab} onValueChange={setPosTab} className="flex-1 flex flex-col overflow-hidden">
            <TabsList className="w-full rounded-none border-b flex-shrink-0">
              <TabsTrigger value="orders" className="flex-1">
                <ShoppingCart className="w-4 h-4 mr-2" />
                Pedidos ({activeOrders.length})
              </TabsTrigger>
              <TabsTrigger value="details" className="flex-1">
                <Users className="w-4 h-4 mr-2" />
                Detalles
              </TabsTrigger>
            </TabsList>

            {/* Orders Tab */}
            <TabsContent value="orders" className="flex-1 m-0 overflow-hidden">
              <ScrollArea className="h-[calc(100vh-180px)]">
                {activeOrders.length === 0 ? (
                  <div className="p-6 text-center">
                    <Bell className="w-12 h-12 mx-auto text-muted-foreground mb-4" />
                    <p className="text-muted-foreground">No hay pedidos pendientes</p>
                  </div>
                ) : (
                  <div className="p-4 space-y-4">
                    <AnimatePresence>
                    {activeOrders.map((order) => {
                      const isNew = newOrderIds.has(order.order_id);
                      return (
                      <motion.div
                        key={order.order_id}
                        initial={isNew ? { scale: 0.9, opacity: 0, y: -20 } : false}
                        animate={{ 
                          scale: 1, 
                          opacity: 1, 
                          y: 0,
                          boxShadow: isNew ? ['0 0 0 0 rgba(59, 130, 246, 0)', '0 0 0 8px rgba(59, 130, 246, 0.3)', '0 0 0 0 rgba(59, 130, 246, 0)'] : 'none'
                        }}
                        exit={{ scale: 0.9, opacity: 0 }}
                        transition={{ duration: 0.4 }}
                      >
                      <Card className={`overflow-hidden ${isNew ? 'ring-2 ring-blue-500' : ''}`}>
                        <CardHeader className="p-4 pb-2">
                          <div className="flex items-center justify-between">
                            <div>
                              <p className="font-mono text-xs text-muted-foreground">
                                #{order.order_id.slice(-6)}
                              </p>
                              <CardTitle className="text-base">
                                {units.find(u => u.unit_id === order.unit_id)?.name || 'Mesa'}
                              </CardTitle>
                            </div>
                            <Badge data-auto-ink style={{ backgroundColor: getStatusColor(order.status), '--ink-on': inkOn(getStatusColor(order.status)) }}>
                              {order.status}
                            </Badge>
                          </div>
                        </CardHeader>
                        <CardContent className="p-4 pt-0">
                          {/* Items */}
                          <div className="space-y-1 mb-3">
                            {order.items?.map((item, idx) => (
                              <div key={idx} className="flex justify-between text-sm">
                                <span>
                                  {item.quantity}x {item.product_name}
                                  {item.variant && <span className="text-muted-foreground"> ({item.variant})</span>}
                                </span>
                                <span className="font-mono">${item.subtotal?.toFixed(2)}</span>
                              </div>
                            ))}
                          </div>
                          
                          {/* Total */}
                          <div className="flex justify-between font-bold border-t pt-2">
                            <span>Total</span>
                            <span className="font-mono">${order.total?.toFixed(2)}</span>
                          </div>

                          {/* Notes */}
                          {order.items?.some(i => i.notes) && (
                            <div className="mt-2 p-2 bg-muted rounded text-sm">
                              <MessageSquare className="w-4 h-4 inline mr-1" />
                              {order.items.filter(i => i.notes).map(i => i.notes).join(', ')}
                            </div>
                          )}

                          {/* Actions */}
                          <div className="flex gap-2 mt-3">
                            <Select
                              value={order.status}
                              onValueChange={(status) => handleStatusChange(order.order_id, status)}
                            >
                              <SelectTrigger className="flex-1">
                                <SelectValue />
                              </SelectTrigger>
                              <SelectContent>
                                {statuses.map((s) => (
                                  <SelectItem key={s.status_id} value={s.name}>
                                    <div className="flex items-center gap-2">
                                      <div 
                                        className="w-3 h-3 rounded-full"
                                        style={{ backgroundColor: s.color }}
                                      />
                                      {s.name}
                                    </div>
                                  </SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                            <Button
                              variant="destructive"
                              size="icon"
                              onClick={() => handleCancelOrder(order.order_id)}
                            >
                              <XCircle className="w-4 h-4" />
                            </Button>
                          </div>
                        </CardContent>
                      </Card>
                      </motion.div>
                      );
                    })}
                    </AnimatePresence>
                  </div>
                )}
              </ScrollArea>
            </TabsContent>

            {/* Details Tab */}
            <TabsContent value="details" className="flex-1 m-0">
              <ScrollArea className="h-full">
                {selectedUnit && unitDetails ? (
                  <div className="p-4">
                    <Card>
                      <CardHeader>
                        <CardTitle>{selectedUnit.name}</CardTitle>
                        <CardDescription>
                          Sesión activa desde {new Date(unitDetails.started_at).toLocaleTimeString('es-AR', { hour: '2-digit', minute: '2-digit', hourCycle: 'h23' })}
                        </CardDescription>
                      </CardHeader>
                      <CardContent className="space-y-4">
                        {/* Participants */}
                        {unitDetails.participants?.length > 0 && (
                          <div>
                            <h4 className="font-semibold mb-2">Participantes</h4>
                            <div className="space-y-2">
                              {unitDetails.participants.map((p) => (
                                <div key={p.participant_id} className="flex justify-between items-center p-2 bg-muted rounded">
                                  <span>{p.name}</span>
                                  <span className="font-mono">${p.subtotal?.toFixed(2) || '0.00'}</span>
                                </div>
                              ))}
                            </div>
                          </div>
                        )}

                        {/* Session Orders */}
                        {unitDetails.orders?.length > 0 && (
                          <div>
                            <h4 className="font-semibold mb-2">Pedidos de la Sesión</h4>
                            <div className="space-y-2">
                              {unitDetails.orders.map((order) => (
                                <div key={order.order_id} className="p-2 bg-muted rounded">
                                  <div className="flex justify-between items-center mb-1">
                                    <Badge data-auto-ink style={{ backgroundColor: getStatusColor(order.status), '--ink-on': inkOn(getStatusColor(order.status)) }}>
                                      {order.status}
                                    </Badge>
                                    <span className="font-mono font-bold">${order.total?.toFixed(2)}</span>
                                  </div>
                                  {order.items?.map((item, idx) => (
                                    <p key={idx} className="text-sm text-muted-foreground">
                                      {item.quantity}x {item.product_name}
                                    </p>
                                  ))}
                                </div>
                              ))}
                            </div>
                          </div>
                        )}

                        {/* Totals */}
                        <div className="border-t pt-4 space-y-2">
                          <div className="flex justify-between">
                            <span>Subtotal</span>
                            <span className="font-mono">${unitDetails.subtotal?.toFixed(2) || '0.00'}</span>
                          </div>
                          {unitDetails.tax > 0 && (
                            <div className="flex justify-between text-sm text-muted-foreground">
                              <span>Impuestos</span>
                              <span className="font-mono">${unitDetails.tax?.toFixed(2)}</span>
                            </div>
                          )}
                          {unitDetails.service_charge > 0 && (
                            <div className="flex justify-between text-sm text-muted-foreground">
                              <span>Servicio</span>
                              <span className="font-mono">${unitDetails.service_charge?.toFixed(2)}</span>
                            </div>
                          )}
                          <div className="flex justify-between font-bold text-lg">
                            <span>Total</span>
                            <span className="font-mono">${unitDetails.total?.toFixed(2) || '0.00'}</span>
                          </div>
                        </div>

                        {/* Actions */}
                        <div className="flex flex-col gap-2">
                          <Button onClick={openAddItemDialog} variant="secondary">
                            <Plus className="w-4 h-4 mr-2" />
                            Agregar Ítems
                          </Button>
                          <Button onClick={() => setShowWhatsAppDialog(true)} variant="outline">
                            <Send className="w-4 h-4 mr-2" />
                            Enviar por WhatsApp
                          </Button>
                          <Button 
                            onClick={handlePrintTicket} 
                            variant="outline"
                            disabled={printingTicket}
                          >
                            <Printer className="w-4 h-4 mr-2" />
                            {printingTicket ? 'Imprimiendo...' : 'Imprimir Ticket'}
                          </Button>
                          <Button onClick={() => setShowCheckoutDialog(true)}>
                            <CreditCard className="w-4 h-4 mr-2" />
                            Cerrar Cuenta
                          </Button>
                        </div>
                      </CardContent>
                    </Card>
                  </div>
                ) : (
                  <div className="p-6 text-center">
                    <Users className="w-12 h-12 mx-auto text-muted-foreground mb-4" />
                    <p className="text-muted-foreground">
                      {selectedUnit 
                        ? 'Esta mesa no tiene sesión activa' 
                        : 'Selecciona una mesa para ver detalles'}
                    </p>
                  </div>
                )}
              </ScrollArea>
            </TabsContent>
          </Tabs>
        </div>
      </div>
      </>)}

      {/* Checkout Dialog */}
      {!isModern && <Dialog open={showCheckoutDialog} onOpenChange={setShowCheckoutDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Cerrar Cuenta</DialogTitle>
            <DialogDescription>
              Total: ${unitDetails?.total?.toFixed(2) || '0.00'}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>Propina</Label>
              <div className="flex gap-2">
                {[0, 10, 15, 20].map((pct) => (
                  <Button
                    key={pct}
                    variant={checkoutTip === (unitDetails?.total || 0) * pct / 100 ? 'default' : 'outline'}
                    size="sm"
                    onClick={() => setCheckoutTip((unitDetails?.total || 0) * pct / 100)}
                  >
                    {pct}%
                  </Button>
                ))}
              </div>
              <Input
                type="number"
                placeholder="Propina manual"
                value={checkoutTip}
                onChange={(e) => setCheckoutTip(parseFloat(e.target.value) || 0)}
              />
            </div>
            <div className="space-y-2">
              <Label>Método de Pago</Label>
              <Select value={paymentMethod} onValueChange={setPaymentMethod}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="cash">Efectivo</SelectItem>
                  <SelectItem value="card">Tarjeta</SelectItem>
                  <SelectItem value="transfer">Transferencia</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="border-t pt-4">
              <div className="flex justify-between text-lg font-bold">
                <span>Total Final</span>
                <span className="font-mono">
                  ${((unitDetails?.total || 0) + checkoutTip).toFixed(2)}
                </span>
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowCheckoutDialog(false)}>
              Cancelar
            </Button>
            <Button onClick={handleCheckout}>
              <CheckCircle className="w-4 h-4 mr-2" />
              Confirmar Pago
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>}

      {/* WhatsApp Dialog */}
      <Dialog open={showWhatsAppDialog} onOpenChange={setShowWhatsAppDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Enviar Cuenta por WhatsApp</DialogTitle>
            <DialogDescription>
              Ingresa el número de teléfono del cliente
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>Número de Teléfono</Label>
              <Input
                placeholder="+54 911 12345678"
                value={whatsAppPhone}
                onChange={(e) => setWhatsAppPhone(e.target.value)}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowWhatsAppDialog(false)}>
              Cancelar
            </Button>
            <Button onClick={handleSendWhatsApp}>
              <Send className="w-4 h-4 mr-2" />
              Enviar
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Add Items Dialog */}
      <Dialog open={showAddItemDialog} onOpenChange={setShowAddItemDialog}>
        <DialogContent className="max-w-2xl max-h-[80vh]">
          <DialogHeader>
            <DialogTitle>Agregar Ítems a {selectedUnit?.name}</DialogTitle>
            <DialogDescription>
              Selecciona los productos a agregar
            </DialogDescription>
          </DialogHeader>
          <div className="py-4">
            <ScrollArea className="h-[400px] pr-4">
              {menu.categories.map((category) => {
                const categoryProducts = menu.products.filter(p => p.category_id === category.category_id);
                if (categoryProducts.length === 0) return null;
                
                return (
                  <div key={category.category_id} className="mb-6">
                    <h4 className="font-semibold mb-3 text-sm uppercase text-muted-foreground">
                      {category.name}
                    </h4>
                    <div className="grid grid-cols-2 gap-2">
                      {categoryProducts.map((product) => {
                        const selected = selectedProducts.find(p => p.product_id === product.product_id);
                        return (
                          <div
                            key={product.product_id}
                            className={`p-3 rounded-lg border cursor-pointer transition-all ${
                              selected 
                                ? 'border-primary bg-primary/10' 
                                : 'border-border hover:border-primary/50'
                            }`}
                            onClick={() => toggleProductSelection(product)}
                          >
                            <div className="flex justify-between items-start">
                              <div className="flex-1">
                                <p className="font-medium text-sm">{product.name}</p>
                                <p className="text-sm text-primary font-mono">${product.price.toFixed(2)}</p>
                              </div>
                              {selected && (
                                <div className="flex items-center gap-1">
                                  <Badge variant="secondary" className="font-mono">
                                    x{selected.quantity}
                                  </Badge>
                                  <Button
                                    size="sm"
                                    variant="ghost"
                                    className="h-6 w-6 p-0"
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      incrementProduct(product.product_id);
                                    }}
                                  >
                                    <Plus className="w-3 h-3" />
                                  </Button>
                                </div>
                              )}
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                );
              })}
            </ScrollArea>
            
            {selectedProducts.length > 0 && (
              <div className="mt-4 pt-4 border-t">
                <div className="flex justify-between items-center mb-2">
                  <span className="text-sm text-muted-foreground">
                    {selectedProducts.reduce((acc, p) => acc + p.quantity, 0)} productos seleccionados
                  </span>
                  <span className="font-bold">
                    Total: ${selectedProducts.reduce((acc, p) => acc + (p.price * p.quantity), 0).toFixed(2)}
                  </span>
                </div>
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowAddItemDialog(false)}>
              Cancelar
            </Button>
            <Button 
              onClick={handleAddItemsToSession} 
              disabled={selectedProducts.length === 0 || addingItems}
            >
              {addingItems ? (
                <>
                  <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                  Agregando...
                </>
              ) : (
                <>
                  <Plus className="w-4 h-4 mr-2" />
                  Agregar ({selectedProducts.reduce((acc, p) => acc + p.quantity, 0)})
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Ticket Preview Modal */}
      <Dialog open={showTicketPreview} onOpenChange={setShowTicketPreview}>
        <DialogContent className="max-w-md max-h-[90vh] overflow-hidden flex flex-col">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Printer className="w-5 h-5 text-blue-500" />
              Vista Previa del Ticket
            </DialogTitle>
          </DialogHeader>
          
          <div className="flex-1 overflow-auto bg-white rounded-lg border shadow-inner my-4" style={{ maxHeight: '50vh' }}>
            <iframe
              srcDoc={ticketHtml}
              title="Ticket Preview"
              className="w-full h-full min-h-[400px]"
              style={{ border: 'none' }}
            />
          </div>
          
          <DialogFooter className="flex gap-2">
            <Button variant="outline" onClick={() => setShowTicketPreview(false)}>
              Cancelar
            </Button>
            <Button 
              onClick={handleConfirmPrint}
              className="bg-gradient-to-r from-blue-500 to-cyan-600"
            >
              <Printer className="w-4 h-4 mr-2" />
              Imprimir
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <TableActionsDialog
        unit={actionUnit}
        units={tableUnits}
        open={!!actionUnit}
        onOpenChange={(v) => !v && setActionUnit(null)}
        onDone={() => { refreshTables(); fetchDashboard(); }}
        onOpenDetails={(u) => handleUnitClick(toLegacyUnit(u))}
        onOpenTable={(u) => handleCreateSession(toLegacyUnit(u))}
        onRelease={(u) => handleQuickRelease(toLegacyUnit(u))}
      />
    </div>
  );
}
